import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import ProductGrid from '../../components/ProductGrid/ProductGrid';
import './Shop.css';
import { getAllProducts, getProductsByCategory } from '../../services/ProductService';
import { getAllCategories } from '../../services/CategoryService';

const Shop = ({ addToCart }) => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();

  // Load all products initially
  useEffect(() => {
    const loadProducts = async () => {
      try {
        setLoading(true);
        // Fetch products
        const allProducts = await getAllProducts();
        setProducts(allProducts);

        // Check if there's a category in the query parameters
        const queryParams = new URLSearchParams(location.search);
        const categoryParam = queryParams.get('category');

        if (categoryParam) {
          setActiveCategory(categoryParam);
          // Instead of calling filterProducts which is a function, directly fetch the filtered products here
          const categoryProducts = await getProductsByCategory(categoryParam);
          setFilteredProducts(categoryProducts);
        } else {
          setFilteredProducts(allProducts);
        }
      } catch (err) {
        console.error('Error loading products:', err);
        setError('Failed to load products: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
  }, [location.search]);

  // Load categories
  useEffect(() => {
    const loadCategories = async () => {
      try {
        console.log('Loading categories...');
        const categoriesData = await getAllCategories();
        console.log('Categories loaded:', categoriesData);
        
        if (categoriesData && categoriesData.length > 0) {
          setCategories(categoriesData);
        } else {
          setError('No categories available. Please create categories first.');
        }
      } catch (err) {
        console.error('Error loading categories:', err);
        setError('Failed to load categories: ' + err.message);
      }
    };

    loadCategories();
  }, []);

  // Handling category filter click
  const handleCategoryFilter = async (categoryId) => {
    try {
      setLoading(true);
      setActiveCategory(categoryId);
      
      if (categoryId === 'all') {
        setFilteredProducts(products);
      } else {
        console.log("Filtering products with Category Id:", categoryId);
        const filtered = await getProductsByCategory(categoryId);
        console.log("Filtered products:", filtered);
        setFilteredProducts(filtered);
      }
      
      // Reset search when changing categories
      setSearchQuery('');
    } catch (err) {
      console.error('Error filtering products:', err);
      setError('Failed to filter products: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
    
    if (query === '') {
      // If search is cleared, show products for the active category
      if (activeCategory === 'all') {
        setFilteredProducts(products);
      } else {
        // We need to re-fetch category products, but don't want to set loading state
        // to avoid UI flicker during search
        getProductsByCategory(activeCategory)
          .then(filtered => setFilteredProducts(filtered))
          .catch(err => console.error('Error re-fetching category products:', err));
      }
    } else {
      // Filter the current displayed products by search query
      const filtered = filteredProducts.filter(product => 
        product.name.toLowerCase().includes(query) ||
        (product.description && product.description.toLowerCase().includes(query))
      );
      setFilteredProducts(filtered);
    }
  };

  return (
    <div className="shop-container">
      {/* Hero Section */}
      <div className="shop-hero">
        <div className="shop-hero-content">
          <h1 className="shop-hero-title">Pharmacy Shop</h1>
          <p className="shop-hero-subtitle">
            Premium medications, discreet packaging, and fast delivery
          </p>
          <div className="shop-search">
            <input
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={handleSearch}
              className="search-input"
            />
            <button className="search-button">
              <svg viewBox="0 0 24 24">
                <path d="M15.5 14h-.79l-.28-.27a6.5 6.5 0 0 0 1.48-5.34c-.47-2.78-2.79-5-5.59-5.34a6.505 6.505 0 0 0-7.27 7.27c.34 2.8 2.56 5.12 5.34 5.59a6.5 6.5 0 0 0 5.34-1.48l.27.28v.79l4.25 4.25c.41.41 1.08.41 1.49 0 .41-.41.41-1.08 0-1.49L15.5 14zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
              </svg>
            </button>
          </div>
        </div>
      </div>

      <div className="shop-main">
        {/* Sidebar */}
        <div className="shop-sidebar">
          <div className="sidebar-section">
            <h3 className="sidebar-title">Categories</h3>
            <ul className="category-list">
              <li>
                <button
                  className={`category-item ${activeCategory === 'all' ? 'active' : ''}`}
                  onClick={() => handleCategoryFilter('all')}
                >
                  <span className="category-icon">🏠</span>
                  <span className="category-name">All Products</span>
                  {activeCategory === 'all' && (
                    <span className="active-indicator"></span>
                  )}
                </button>
              </li>
              {categories.map(category => (
                <li key={category.id}>
                  <button
                    className={`category-item ${activeCategory === category.id ? 'active' : ''}`}
                    onClick={() => handleCategoryFilter(category.id)}
                  >
                    <span className="category-icon">{category.icon || '📦'}</span>
                    <span className="category-name">{category.name}</span>
                    {activeCategory === category.id && (
                      <span className="active-indicator"></span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="sidebar-section">
            <h3 className="sidebar-title">Why Choose Us?</h3>
            <ul className="benefits-list">
              <li className="benefit-item">
                <svg viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
                FDA-approved medications
              </li>
              <li className="benefit-item">
                <svg viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
                Discreet packaging
              </li>
              <li className="benefit-item">
                <svg viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
                Fast shipping
              </li>
              <li className="benefit-item">
                <svg viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
                Licensed pharmacists
              </li>
            </ul>
          </div>
        </div>

        {/* Products Section */}
        <div className="shop-products">
          <div className="products-header">
            <h2 className="products-title">
              {activeCategory === 'all' ? 'All Products' : 
               categories.find(c => c.id === activeCategory)?.name || 'Products'}
            </h2>
            <div className="products-count">
              {filteredProducts.length} {filteredProducts.length === 1 ? 'item' : 'items'}
            </div>
          </div>

          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading products...</p>
            </div>
          ) : error ? (
            <div className="error-state">
              <p>{error}</p>
              <button 
                className="reset-button"
                onClick={() => handleCategoryFilter('all')}
              >
                Try Again
              </button>
            </div>
          ) : filteredProducts.length > 0 ? (
            <ProductGrid
              products={filteredProducts}
              addToCart={addToCart}
              gridLayout="responsive"
            />
          ) : (
            <div className="empty-state">
              <svg viewBox="0 0 24 24">
                <path d="M22 7h-6V5c0-.55-.22-1.05-.59-1.41C15.05 3.22 14.55 3 14 3h-4c-1.1 0-2 .9-2 2v2H2v15h20V7zM10 5h4v2h-4V5zm1 13.5l-1-1 3-3-3-3 1-1 4 4-4 4z"/>
              </svg>
              <h3>No products found</h3>
              <p>We couldn't find any products matching your criteria.</p>
              <button 
                className="reset-button"
                onClick={() => handleCategoryFilter('all')}
              >
                Reset Filters
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Shop;